import React, { useContext, useState, useEffect } from 'react'
import AuthContext from '../../context/AuthContext'
import { InlineLoading, Breadcrumb, BreadcrumbItem, Tile, Form, FormGroup, TextInput, Select, SelectItem, Button, InlineNotification, NotificationActionButton } from 'carbon-components-react'
import Toast from '../../components/Notification/Toast';
import { sleep } from '../../utils/functions/tools/sleep';
const { REACT_APP_DEV_API_URL, REACT_APP_PROD_API_URL, NODE_ENV } = process.env;
const API_URL =
  NODE_ENV === 'production' ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_DEV_API_URL;

const EmailSettings = ({ match }) => {

  // Variables for modals and toasts
  const [error, setError] = useState(false);
  const [list, setList] = useState([]);
  let toastProperties = null;

  // Other Variables
  let { authTokens } = useContext(AuthContext)
  let [emailSettings, setEmailSettings] = useState([])

  // Inline loading for Test Email
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [description, setDescription] = useState('Sending...');
  const [ariaLive, setAriaLive] = useState('off');
  const [inlineLoadingState, setInlineLoadingState] = useState('');

  useEffect(() => {
    const getEmail = async () => {
      let response = await fetch(`${API_URL}/system/settings/email/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authTokens?.access
        }
      })
      let data = await response.json()
      setEmailSettings(data)
    }
    getEmail()
    document.title = "Email Settings | WillowBMS"
  }, [])

  const showToast = (type, title, description) => {
    const id = Math.floor((Math.random() * 100) + 1);
    const time = new Date().toLocaleString();

    switch (type) {
      case 'success':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'success',
          time: time
        }
        break;
      case 'error':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'error',
          time: time
        }
        break;
      case 'info':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'info',
          time: time
        }
        break;
      case 'warning':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'warning',
          time: time
        }
        break;
      default:
        setList([]);
    }
    setList([...list, toastProperties]);
  }

  let updateEmail = async (values) => {
    values.preventDefault();

    let tls = false
    let ssl = false

    if (values.target.encryption.value === "TLS") {
      tls = true
      ssl = false
    } else if (values.target.encryption.value === "SSL") {
      tls = false
      ssl = true
    }

    let response = await fetch(`${API_URL}/system/settings/email/`, {
      method: "PUT",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authTokens?.access
      },
      body: JSON.stringify({
        'id': 1,
        'smtphost': values.target.smtphost.value,
        'smtpport': values.target.smtpport.value,
        'email': values.target.smtpemail.value,
        'defaultFrom': values.target.smtpemail.value,
        'useTLS': tls,
        'useSSL': ssl,
        'smtppassword': values.target.smtppassword.value
      })
    })

    if (response.status === 200) {
      setError(false)
      showToast("success", "Success", "SMTP settings have been saved.")
    } else {
      setError(true)
    }
  }

  let testEmail = async (values) => {
    values.preventDefault();

    setIsSubmitting(true);
    setAriaLive('assertive');

    let response = await fetch(`${API_URL}/system/settings/email/test/`, {
      method: "PUT",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authTokens?.access
      },
      body: JSON.stringify({
        'email': values.target.testEmail.value
      })
    })

    if (response.status === 200) {
      showToast("success", "Success", "SMTP test email has been sent.")

      setIsSubmitting(false);
      setSuccess(true);
      setDescription('Sent!');
      setInlineLoadingState("finished")
      await sleep(2000)
      setSuccess(false);
      setDescription('Sending...');
      setAriaLive('off');
      setInlineLoadingState("")
    } else {
      showToast("error", "Error", "Unable to send the SMTP test email. Please check the settings and try again.")

      setIsSubmitting(false);
      setSuccess(true);
      setDescription('Something went wrong.');
      setInlineLoadingState("error")
      await sleep(2000)
      setSuccess(false);
      setDescription('Sending...');
      setAriaLive('off');
      setInlineLoadingState("")
    }
  }

  return (
    <div id="body" className="bms-page d-flex">
      <Toast toastList={list} />
      <div className="wrapper">
        <Breadcrumb>
          <BreadcrumbItem href="/system/settings/general">Settings</BreadcrumbItem>
          <span>Email (SMTP)</span>
        </Breadcrumb>
        <h2>
          Settings
        </h2>
        <Tile light={true} className="row mt-1">
          <h5 className="tile-title">Email (SMTP)</h5>
          {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to save email (SMTP) settings" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
          <Form onSubmit={updateEmail}>
            <FormGroup className="d-flex flex-w">
              <div className="col-12 col-md-6 m-005">
                <Select labelText="Email Encryption" name="encryption" id="encryption">
                  <SelectItem selected={emailSettings?.useSSL === true ? "selected" : ""} value="SSL" text="SSL" />
                  <SelectItem selected={emailSettings?.useTLS === true ? "selected" : ""} value="TLS" text="TLS" />
                </Select>
              </div>
              <div className="col-12 col-md-6 m-005">
                <TextInput
                  id="smtphost"
                  invalidText="SMTP hostname is required"
                  labelText="SMTP Hostname"
                  placeholder="Enter the SMTP hostname"
                  defaultValue={emailSettings?.smtphost}
                  required
                />
              </div>
              <div className="col-12 col-md-6 m-005">
                <TextInput
                  id="smtpport"
                  invalidText="SMTP port is required"
                  labelText="SMTP Port"
                  placeholder="Enter the SMTP port"
                  defaultValue={emailSettings?.smtpport}
                  required
                />
              </div>
              <div className="col-12 col-md-6 m-005">
                <TextInput
                  id="smtpemail"
                  labelText="SMTP Email Address (optional)"
                  helperText="Only needed if required for SMTP authentication"
                  placeholder="Enter the SMTP Email Address"
                  defaultValue={emailSettings?.email}
                />
              </div>
              <div className="col-12 col-md-6 m-005">
                <TextInput.PasswordInput
                  id="smtppassword"
                  labelText="SMTP Password (optional)"
                  helperText="Only needed if required for SMTP authentication"
                  placeholder="Enter the SMTP Password"
                  defaultValue={emailSettings?.smtppassword}
                />
              </div>
              <div className="col-12 m-005">
                <Button type="submit">
                  Save Changes
                </Button>
              </div>
            </FormGroup>
          </Form>
        </Tile>
        <Tile light={true} className="row mt-1">
          <h5 className="tile-title">Test Email Settings</h5>
          {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to save email (SMTP) settings" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
          <Form onSubmit={testEmail}>
            <FormGroup className="d-flex flex-w">
              <div className="col-12 col-md-6 m-005">
                <TextInput
                  id="testEmail"
                  type="email"
                  labelText="Recipient Email Address"
                  invalidText="Email address is required for the email test"
                  helperText="Input the email address to receive an email to test your SMTP settings"
                  placeholder="Enter an email address"
                  required
                />
              </div>
              <div className="col-12 m-005">
                {isSubmitting || success ? (
                  <InlineLoading
                    style={{ marginLeft: '1rem' }}
                    description={description}
                    status={success ? inlineLoadingState : 'active'}
                    aria-live={ariaLive}
                  />
                ) : (
                  <Button type="submit">Submit</Button>
                )}
              </div>
            </FormGroup>
          </Form>
        </Tile>
      </div>
    </div>
  )
}

export default EmailSettings